import { FC, MouseEvent } from 'react'
import Tooltip from 'rc-tooltip'
import styles from './tag.module.scss'

interface TagProps {
  backgroundColor?: string
  color?: string
  cursor?: string
  fontSize?: string
  fontWeight?: number
  handleClick?: (value?: string) => void
  height?: string
  icon?: string
  iconBackgroundColor?: string
  iconSVG?: React.ComponentType
  /** Visible text when it differs from `title`. `title` stays the value
      reported to `handleClick`, so a tag can be relabelled without changing
      what it stands for. */
  label?: string
  lineHeight?: string
  marginRight?: string
  marginTop?: string
  number?: number
  numberColor?: string
  /** Fired when the small icon/quantity badge is clicked (separate from the tag body). */
  onIconClick?: () => void
  padding?: string
  quantity?: string
  title?: string
  tooltipContent?: string
}

export const Tag: FC<TagProps> = ({
  color,
  iconSVG: IconSVG,
  backgroundColor,
  title,
  icon,
  iconBackgroundColor,
  number,
  numberColor,
  padding,
  height,
  fontWeight,
  fontSize,
  lineHeight,
  marginRight,
  marginTop,
  cursor,
  label,
  handleClick,
  onIconClick,
  tooltipContent,
  quantity,
}) => {
  function handleClickButton(): void {
    const value = title ?? undefined

    if (handleClick) {
      handleClick(value)
    }
  }

  function handleIconClick(e: MouseEvent): void {
    if (!onIconClick) {
      return
    }

    // Keep the tag-body click from also firing.
    e.stopPropagation()
    onIconClick()
  }

  // Block the popup's document-level mousedown outside-close so the icon's
  // click can toggle it cleanly (otherwise it closes on mousedown, then the
  // click reopens it).
  function handleIconMouseDown(e: MouseEvent): void {
    if (!onIconClick) {
      return
    }

    e.stopPropagation()
  }

  return (
    <Tooltip
      mouseLeaveDelay={0}
      overlay={<div className={styles.tooltip}>{tooltipContent}</div>}
      placement="top"
      trigger={tooltipContent ? ['hover'] : []}
    >
      <div
        className={styles.tag}
        onClick={handleClickButton}
        style={{
          backgroundColor,
          color,
          cursor: cursor ?? 'auto',
          fontSize: fontSize ?? 'inherit',
          fontWeight: fontWeight ?? 400,
          height: height ?? '26px',
          lineHeight: lineHeight ?? '14.06px',
          marginRight: marginRight ?? '8px',
          marginTop: marginTop ?? '0px',
          padding: padding || '6px 8px',
          paddingLeft: icon || number ? '3px' : 'auto',
        }}
      >
        <span
          className={styles.tagIcon}
          style={{
            backgroundColor: iconBackgroundColor ?? 'transparent',
            color: numberColor ?? '',
            display: icon || number ? 'inline-flex' : 'none',
          }}
        >
          {icon && !IconSVG && <img alt="icon" src={icon} />}

          {number || null}
        </span>
        {label ?? title}{' '}
        {quantity && quantity !== '0' && (
          <span
            className={styles.quantity}
            onClick={onIconClick ? handleIconClick : undefined}
            onMouseDown={onIconClick ? handleIconMouseDown : undefined}
          >
            <p>{quantity}</p>
          </span>
        )}
        {quantity === '0' && IconSVG && (
          <div
            className={styles.svgWrapper}
            onClick={onIconClick ? handleIconClick : undefined}
            onMouseDown={onIconClick ? handleIconMouseDown : undefined}
            style={{
              backgroundColor: iconBackgroundColor ?? 'transparent',
              color: numberColor ?? '',
            }}
          >
            <IconSVG />
          </div>
        )}
      </div>
    </Tooltip>
  )
}
